import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import raf from 'raf';
import { observer } from 'mobx-react';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import { action, observable, runInAction, toJS } from 'mobx';
import { DragDropContext, } from 'react-beautiful-dnd';
import warning from 'choerodon-ui/lib/_util/warning';
import { isCalcSize, isPercentSize, pxToRem, toPx } from 'choerodon-ui/lib/_util/UnitConvertor';
import measureScrollbar from 'choerodon-ui/lib/_util/measureScrollbar';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import ReactResizeObserver from 'choerodon-ui/lib/_util/resizeObserver';
import Animate from 'choerodon-ui/lib/animate';
import Icon from 'choerodon-ui/lib/icon';
import Column from './Column';
import TableRow from './TableRow';
import TableHeaderCell from './TableHeaderCell';
import TableStore from './TableStore';
import TableHeader from './TableHeader';
import autobind from '../_util/autobind';
import Pagination from '../pagination/Pagination';
import Spin from '../spin';
import DataSetComponent from '../data-set/DataSetComponent';
import { TableContextProvider } from './TableContext';
import TableWrapper from './TableWrapper';
import Profiler from './Profiler';
import TableTBody from './TableTBody';
import ExpandableTableTBody from './ExpandableTableTBody';
import TableFooter from './TableFooter';
import { ColumnLock, DragColumnAlign, HighLightRowType, RowBoxPlacement, ScrollPosition, SelectionMode, TableButtonType, TableEditMode, TableHeightType, TableMode, TablePaginationPosition, TableQueryBarType, } from './enum';
import TableQueryBar from './query-bar';
import ToolBar from './query-bar/TableToolBar';
import FilterBar from './query-bar/TableFilterBar';
import AdvancedQueryBar from './query-bar/TableAdvancedQueryBar';
import ProfessionalBar from './query-bar/TableProfessionalBar';
import DynamicFilterBar from './query-bar/TableDynamicFilterBar';
import { findCell, findIndexedSibling, findRow, getHeight, getPaginationPosition, isCanEdictingRow, isDropresult, isStickySupport, onlyCustomizedColumn, } from './utils';
import TableBody from './TableBody';
import VirtualWrapper from './VirtualWrapper';
import SelectionTips from './SelectionTips';
import { DataSetEvents, DataSetSelection } from '../data-set/enum';
import { Size } from '../core/enum';
import StickyShadow from './StickyShadow';
import { getUniqueFieldNames } from '../data-set/utils';
import mergeProps from '../_util/mergeProps';
export const buttonsEnumType = PropTypes.oneOf([
    TableButtonType.add,
    TableButtonType.save,
    TableButtonType.remove,
    TableButtonType.delete,
    TableButtonType.reset,
    TableButtonType.query,
    TableButtonType.export,
    TableButtonType.expandAll,
    TableButtonType.collapseAll,
]);
let _instance;
// 构造一个单例table来防止body下不能有table元素的报错
export const instance = (wrapperClassName, prefixCls) => {
    // Using a table as the portal so that we do not get react
    // warnings when mounting a tr element
    const _tableContain = () => {
        const table = document.createElement('table');
        if (wrapperClassName) {
            table.className = wrapperClassName;
        }
        const thead = document.createElement('thead');
        thead.className = `${prefixCls}-thead`;
        table.appendChild(thead);
        const headtr = document.createElement('tr');
        thead.appendChild(headtr);
        const tbody = document.createElement('tbody');
        tbody.className = `${prefixCls}-tbody`;
        table.appendChild(tbody);
        if (!document.body) {
            throw new Error('document.body required a body to append');
        }
        document.body.appendChild(table);
        return {
            tbody,
            headtr,
        };
    };
    if (_instance) {
        return _instance;
    }
    return _instance = _tableContain();
};
let Table = class Table extends DataSetComponent {
    constructor() {
        super(...arguments);
        this.tableStore = new TableStore(this);
        this.wrapperWidth = [];
    }
    get currentRow() {
        const { prefixCls } = this;
        return this.element.querySelector(`.${prefixCls}-row-current`);
    }
    get firstRow() {
        const { prefixCls } = this;
        return this.element.querySelector(`.${prefixCls}-row:first-child`);
    }
    get lastRow() {
        const { prefixCls } = this;
        return this.element.querySelector(`.${prefixCls}-row:last-child`);
    }
    saveResizeRef(node) {
        this.resizeLine = node;
    }
    saveContentRef(node) {
        this.tableContentWrap = node;
    }
    useFocusedClassName() {
        return false;
    }
    setCode(props) {
        const { customizedCode } = props;
        if (customizedCode) {
            this.code = customizedCode;
        }
        else {
            super.setCode(props);
        }
    }
    handleResize(width) {
        const { element, tableStore, wrapperWidth } = this;
        if (width !== undefined) {
            const duplicate = wrapperWidth.includes(width);
            wrapperWidth.unshift(width);
            window.clearTimeout(this.wrapperWidthTimer);
            this.wrapperWidthTimer = window.setTimeout(() => {
                wrapperWidth.pop();
            }, 500);
            if (wrapperWidth.length > 2) {
                wrapperWidth.pop();
            }
            if (duplicate) {
                return;
            }
        }
        if (!element.offsetParent) {
            tableStore.styledHidden = true;
        }
        else if (!tableStore.hidden) {
            this.syncSizeInFrame(width);
        }
        else {
            tableStore.styledHidden = false;
        }
    }
    handleWindowResize() {
        this.handleResize();
    }
    handleDataSetLoad() {
        const { tableStore } = this;
        if (tableStore.performanceEnabled) {
            tableStore.performanceOn = true;
        }
        this.initDefaultExpandedRows();
    }
    handleDataSetCreate({ record, dataSet }) {
        const { tableStore } = this;
        if (tableStore.inlineEdit) {
            if (tableStore.currentEditRecord) {
                tableStore.currentEditRecord.reset();
                dataSet.remove(record);
            }
            else {
                tableStore.currentEditRecord = record;
            }
        }
    }
    handleDataSetValidate({ valid, dataSet, errors: validationErrors, noLocate }) {
        if (!noLocate && !valid) {
            const { tableStore } = this;
            const [firstInvalidRecord] = validationErrors;
            if (firstInvalidRecord) {
                const { errors, record } = firstInvalidRecord;
                if (errors.length) {
                    if (!tableStore.showCachedSelection) {
                        if (dataSet.cachedRecords.includes(record)) {
                            runInAction(() => {
                                tableStore.showCachedSelection = true;
                            });
                        }
                    }
                    const [{ field: { name } }] = errors;
                    if (tableStore.virtual && !findRow(tableStore, record)) {
                        const { tableBodyWrap } = this;
                        if (tableBodyWrap) {
                            tableBodyWrap.scrollTop = record.index * tableStore.virtualRowHeight;
                        }
                    }
                    raf(() => {
                        const cell = findCell(tableStore, name, undefined, record);
                        if (cell) {
                            cell.focus();
                        }
                    });
                }
            }
        }
    }
    handleDataSetSelfValidate({ valid }) {
        this.showDataSetError = !valid;
    }
    handleDataSetReset() {
        this.clearError();
    }
    handleKeyDown(e) {
        const { tableStore } = this;
        const { keyboard } = tableStore;
        if (!tableStore.editing) {
            try {
                const { dataSet } = this.props;
                const ctrlKey = e.ctrlKey || e.metaKey;
                const altKey = e.altKey;
                const shiftKey = e.shiftKey;
                switch (e.keyCode) {
                    case KeyCode.UP:
                        if (shiftKey && keyboard) {
                            this.handleKeyDownUpShift(e);
                        }
                        else {
                            this.handleKeyDownUp(e);
                        }
                        break;
                    case KeyCode.DOWN:
                        if (shiftKey && keyboard) {
                            this.handleKeyDownDownShift(e);
                        }
                        else {
                            this.handleKeyDownDown(e);
                        }
                        break;
                    case KeyCode.RIGHT:
                        this.handleKeyDownRight(e);
                        break;
                    case KeyCode.LEFT:
                        this.handleKeyDownLeft(e);
                        break;
                    case KeyCode.PAGE_UP:
                        e.preventDefault();
                        dataSet.prePage();
                        break;
                    case KeyCode.PAGE_DOWN:
                        e.preventDefault();
                        dataSet.nextPage();
                        break;
                    case KeyCode.HOME:
                        this.handleKeyDownHome(e);
                        break;
                    case KeyCode.END:
                        this.handleKeyDownEnd(e);
                        break;
                    case KeyCode.S:
                        if (ctrlKey === true && keyboard)
                            this.handleKeyDownCTRLS(e);
                        break;
                    case KeyCode.N:
                        if (altKey === true && keyboard)
                            this.handleKeyDownCTRLN(e);
                        break;
                    case KeyCode.D:
                        if (ctrlKey === true && keyboard)
                            this.handleKeyDownCTRLD(e);
                        break;
                    case KeyCode.DELETE:
                        if (altKey === true && keyboard)
                            this.handleKeyDownCTRLDELETE(e);
                        break;
                    default:
                }
            }
            catch (error) {
                warning(false, error.message);
            }
        }
        const { onKeyDown = noop } = this.props;
        onKeyDown(e);
    }
    focusRow(row) {
        if (row) {
            const { index } = row.dataset;
            if (index) {
                const { dataSet } = this.props;
                const record = dataSet.findRecordById(index);
                if (record) {
                    dataSet.current = record;
                    return record;
                }
            }
        }
    }
    async handleKeyDownHome(e) {
        e.preventDefault();
        const { dataSet } = this.props;
        if (!this.tableStore.isTree) {
            await dataSet.first();
        }
        this.focusRow(this.firstRow);
    }
    async handleKeyDownEnd(e) {
        e.preventDefault();
        const { dataSet } = this.props;
        if (!this.tableStore.isTree) {
            await dataSet.last();
        }
        this.focusRow(this.lastRow);
    }
    async handleKeyDownCTRLS(e) {
        e.preventDefault();
        const { tableStore: { dataSet }, } = this;
        dataSet.submit();
    }
    async handleKeyDownCTRLN(e) {
        const { tableStore: { dataSet, editors }, } = this;
        if (editors.size) {
            e.preventDefault();
            dataSet.create({}, 0);
        }
    }
    // TODO: To be optimized
    async handleKeyDownCTRLD(e) {
        e.preventDefault();
        const { currentRow, tableStore } = this;
        const { dataSet } = tableStore;
        let currentElementSibling = null;
        // to justice it can be change or standards compliant
        if (isCanEdictingRow(currentRow)) {
            currentElementSibling = currentRow;
        }
        if (currentElementSibling && tableStore && dataSet) {
            const previousElementSibling = findIndexedSibling(currentElementSibling, -1);
            if (previousElementSibling) {
                const { index } = previousElementSibling.dataset;
                const { index: currentIndex } = currentElementSibling.dataset;
                if (index && currentIndex) {
                    const record = dataSet.findRecordById(index);
                    const currentRecord = dataSet.findRecordById(currentIndex);
                    // exculde the primery key and merge has columns which has edictor
                    if (record && currentRecord && tableStore) {
                        const cloneRecodData = record.clone().toData() || {};
                        const dealCloneRecodData = {};
                        const editeColumn = tableStore.columns
                            .filter((column) => !!column.editor)
                            .reduce((accumulator, nowValue) => [...accumulator, nowValue.name], [])
                            .filter((v) => !(v === null || v === undefined || v === ''));
                        if (editeColumn && editeColumn.length > 0) {
                            editeColumn.forEach(element => {
                                if (element) {
                                    dealCloneRecodData[element] = cloneRecodData[element];
                                }
                            });
                        }
                        // remove the unique name of fields
                        const uniqueFieldNames = getUniqueFieldNames(dataSet);
                        if (uniqueFieldNames && uniqueFieldNames.length > 0) {
                            uniqueFieldNames.forEach(element => {
                                if (element) {
                                    delete dealCloneRecodData[element];
                                }
                            });
                        }
                        currentRecord.set(dealCloneRecodData);
                    }
                }
            }
        }
    }
    async handleKeyDownCTRLDELETE(e) {
        e.preventDefault();
        const { tableStore: { dataSet }, } = this;
        dataSet.delete(dataSet.selected);
    }
    async handleKeyDownUp(e) {
        e.preventDefault();
        const { currentRow } = this;
        let returnRecod;
        if (currentRow) {
            const previousElementSibling = findIndexedSibling(currentRow, -1);
            if (previousElementSibling) {
                returnRecod = this.focusRow(previousElementSibling);
            }
            else {
                const { dataSet } = this.props;
                await dataSet.prePage();
                returnRecod = this.focusRow(this.lastRow);
            }
            if (returnRecod) {
                return Promise.resolve(returnRecod);
            }
        }
        return Promise.reject();
    }
    async handleKeyDownDown(e) {
        e.preventDefault();
        const { currentRow } = this;
        let returnRecod;
        if (currentRow) {
            const nextElementSibling = findIndexedSibling(currentRow, 1);
            if (nextElementSibling) {
                returnRecod = this.focusRow(nextElementSibling);
            }
            else {
                const { dataSet } = this.props;
                await dataSet.nextPage();
                returnRecod = this.focusRow(this.firstRow);
            }
            if (returnRecod) {
                return Promise.resolve(returnRecod);
            }
        }
        return Promise.reject();
    }
    async handleKeyDownDownShift(e) {
        e.preventDefault();
        const { dataSet } = this.tableStore;
        const { currentRow } = this;
        if (currentRow && isCanEdictingRow(currentRow)) {
            const { index } = currentRow.dataset;
            if (index) {
                const record = dataSet.findRecordById(index);
                if (record) {
                    if (!record.selectable) {
                        this.handleKeyDownDown(e);
                    }
                    else if (!record.isSelected) {
                        dataSet.select(record);
                    }
                    else {
                        const currentRecord = await this.handleKeyDownDown(e);
                        if (currentRecord && dataSet) {
                            dataSet.select(currentRecord);
                        }
                    }
                }
            }
        }
        else {
            this.handleKeyDownDown(e);
        }
    }
    async handleKeyDownUpShift(e) {
        e.preventDefault();
        const { dataSet } = this.tableStore;
        const { currentRow } = this;
        if (currentRow && isCanEdictingRow(currentRow)) {
            const { index } = currentRow.dataset;
            if (index) {
                const record = dataSet.findRecordById(index);
                if (record) {
                    if (!record.selectable) {
                        this.handleKeyDownUp(e);
                    }
                    else if (!record.isSelected) {
                        dataSet.select(record);
                    }
                    else {
                        const currentRecord = await this.handleKeyDownUp(e);
                        if (currentRecord) {
                            dataSet.select(currentRecord);
                        }
                    }
                }
            }
        }
        else {
            this.handleKeyDownUp(e);
        }
    }
    handleKeyDownRight(e) {
        const { tableStore, props: { expandedRowRenderer, dataSet }, } = this;
        if (tableStore.isTree || expandedRowRenderer) {
            const { current } = dataSet;
            if (current) {
                e.preventDefault();
                tableStore.setRowExpanded(current, true);
            }
        }
    }
    handleKeyDownLeft(e) {
        const { tableStore, props: { expandedRowRenderer, dataSet }, } = this;
        if (tableStore.isTree || expandedRowRenderer) {
            const { current } = dataSet;
            if (current) {
                e.preventDefault();
                tableStore.setRowExpanded(current, false);
            }
        }
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'columns',
            'header',
            'showHeader',
            'footer',
            'border',
            'columnEditorBorder',
            'selectionMode',
            'rowBoxPlacement',
            'alwaysShowRowBox',
            'showSelectionTips',
            'showSelectionCachedButton',
            'showCachedSelection',
            'onShowCachedSelectionChange',
            'showAllPageSelectionButton',
            'onRow',
            'onColumnResize',
            'rowRenderer',
            'buttons',
            'buttonsLimit',
            'rowHeight',
            'headerRowHeight',
            'footerRowHeight',
            'queryFields',
            'queryFieldsLimit',
            'summaryFieldsLimit',
            'summaryBarFieldWidth',
            'queryBar',
            'queryBarProps',
            'autoFocus',
            'summaryBar',
            'defaultRowExpanded',
            'expandRowByClick',
            'expandedRowRenderer',
            'expandIconColumnIndex',
            'indentSize',
            'filter',
            'mode',
            'editMode',
            'filterBarFieldName',
            'filterBarPlaceholder',
            'pagination',
            'highLightRow',
            'selectedHighLightRow',
            'columnResizable',
            'columnTitleEditable',
            'pristine',
            'expandIcon',
            'spin',
            'virtual',
            'virtualCell',
            'virtualSpin',
            'autoWidth',
            'autoHeight',
            'autoFootHeight',
            'useMouseBatchChoose',
            'autoMaxWidth',
            'dragColumnAlign',
            'columnDraggable',
            'rowDraggable',
            'onDragEnd',
            'rowDragRender',
            'columnsDragRender',
            'editorNextKeyEnterDown',
            'onDragEndBefore',
            'keyboard',
            'dynamicFilterBar',
            'parityRow',
            'rowNumber',
            'treeAsync',
            'treeLoadData',
            'customizable',
            'customizedCode',
            'dragColumn',
            'dragRow',
            'clientExportQuantity',
            'treeQueryExpanded',
            'summary',
            'aggregation',
            'onAggregationChange',
            'showRemovedRow',
            'searchCode',
            'groups',
            'onScrollLeft',
            'onScrollTop',
            'bodyExpandable',
            'defaultBodyExpanded',
            'bodyExpanded',
            'onBodyExpanded',
        ]);
    }
    getWrapperProps(props = {}) {
        const { autoWidth } = this.props;
        const wrapperProps = {
            ...props,
        };
        if (autoWidth) {
            const { columnGroups, overflowY, border } = this.tableStore;
            wrapperProps.style = {
                width: columnGroups.width + (overflowY ? measureScrollbar() : 0) + (border ? 2 : 0),
            };
        }
        return super.getWrapperProps(wrapperProps);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        otherProps.onKeyDown = this.handleKeyDown;
        const { rowHeight } = this.tableStore;
        if (rowHeight === 'auto') {
            delete otherProps.style;
        }
        else {
            otherProps.style = { lineHeight: pxToRem(rowHeight) };
        }
        return otherProps;
    }
    getClassName() {
        const { tableStore: { border, parityRow, aggregation, size }, prefixCls, } = this;
        return super.getClassName({
            [`${prefixCls}-${size}`]: size !== Size.default,
            [`${prefixCls}-bordered`]: border,
            [`${prefixCls}-parity-row`]: parityRow,
            [`${prefixCls}-aggregation`]: aggregation,
        });
    }
    /**
     * 获取传入的 Spin props
     */
    getSpinProps() {
        const { spin, dataSet } = this.props;
        if (spin && !isUndefined(spin.spinning))
            return { ...spin };
        const { loading } = this.tableStore;
        if (loading) {
            return {
                ...spin,
                spinning: true,
            };
        }
        return {
            ...spin,
            dataSet,
        };
    }
    componentWillMount() {
        this.initDefaultExpandedRows();
    }
    componentDidMount() {
        super.componentDidMount();
        this.connect();
        this.syncSize();
        this.syncSizeInFrame();
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        this.disconnect();
        this.tableStore.updateProps(nextProps);
        this.connect();
    }
    componentWillUnmount() {
        this.disconnect();
        if (this.scrollId !== undefined) {
            raf.cancel(this.scrollId);
        }
    }
    syncParentSize(entries) {
        const [entry] = entries;
        const { contentRect: { height } } = entry;
        const { tableStore, element, wrapper } = this;
        const wrapperHeight = wrapper.getBoundingClientRect().height;
        if (wrapperHeight !== height) {
            tableStore.parentHeight = height;
            tableStore.parentPaddingTop =
                element.getBoundingClientRect().top - entry.target.getBoundingClientRect().top;
        }
    }
    connect() {
        this.processDataSetListener(true);
        const { styleMaxHeight, styleMinHeight, styleHeight, heightType } = this.tableStore;
        if ((isString(styleHeight) && isPercentSize(styleHeight)) || (isString(styleMaxHeight) && isPercentSize(styleMaxHeight)) || (isString(styleMinHeight) && isPercentSize(styleMinHeight))) {
            const { wrapper } = this;
            if (wrapper) {
                const { parentNode } = wrapper;
                if (parentNode) {
                    const resizeObserver = new ResizeObserver(this.syncParentSize);
                    resizeObserver.observe(parentNode);
                    this.resizeObserver = resizeObserver;
                }
            }
        }
        if (heightType === TableHeightType.flex || (isString(styleMaxHeight) && isCalcSize(styleMaxHeight)) || (isString(styleMinHeight) && isCalcSize(styleMinHeight))) {
            window.addEventListener('resize', this.handleWindowResize, false);
        }
    }
    disconnect() {
        const { resizeObserver } = this;
        if (resizeObserver) {
            resizeObserver.disconnect();
            delete this.resizeObserver;
        }
        this.processDataSetListener(false);
        window.removeEventListener('resize', this.handleWindowResize, false);
    }
    processDataSetListener(flag) {
        const { dataSet, inlineEdit } = this.tableStore;
        if (dataSet) {
            const handler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            handler.call(dataSet, DataSetEvents.load, this.handleDataSetLoad);
            if (inlineEdit) {
                handler.call(dataSet, DataSetEvents.create, this.handleDataSetCreate);
            }
            handler.call(dataSet, DataSetEvents.validate, this.handleDataSetValidate);
            handler.call(dataSet, DataSetEvents.validateSelf, this.handleDataSetSelfValidate);
            handler.call(dataSet, DataSetEvents.reset, this.handleDataSetReset);
        }
    }
    render() {
        const { tableStore: { overflowX, overflowY, isAnyColumnsLeftLock, isAnyColumnsRightLock }, props: { dataSet, treeQueryExpanded, virtualSpin, buttons, buttonsLimit, queryFields, queryFieldsLimit, summaryFieldsLimit, summaryBarFieldWidth, filterBarFieldName, filterBarPlaceholder, summaryBar, dynamicFilterBar, clientExportQuantity, indentSize, selectionMode, rowRenderer, onRow, onColumnResize, expandedRowRenderer, expandRowByClick, rowDragRender, columnsDragRender, mode, pristine, showSelectionCachedButton, onShowCachedSelectionChange, autoMaxWidth, summary, searchCode, }, tableStore, prefixCls, } = this;
        const content = this.getTable();
        const pagination = this.getPagination(TablePaginationPosition.top);
        const tableSpinProps = mergeProps(this.getContextConfig('tableSpinProps'), this.getSpinProps());
        const tableButtonsLimit = isNil(buttonsLimit) ? this.getContextConfig('tableButtonsLimit') : buttonsLimit;
        return (React.createElement(ReactResizeObserver, { resizeProp: "width", onResize: this.handleResize },
            React.createElement("div", Object.assign({}, this.getWrapperProps()),
                React.createElement(TableContextProvider, { code: this.code, prefixCls: prefixCls, dataSet: dataSet, tableStore: tableStore, indentSize: indentSize, selectionMode: selectionMode, onRow: onRow, onColumnResize: onColumnResize, rowRenderer: rowRenderer, expandedRowRenderer: expandedRowRenderer, expandRowByClick: expandRowByClick, rowDragRender: rowDragRender, columnsDragRender: columnsDragRender, showSelectionCachedButton: showSelectionCachedButton, onShowCachedSelectionChange: onShowCachedSelectionChange, autoMaxWidth: autoMaxWidth, pristine: pristine, summary: summary, virtualSpin: virtualSpin, spinProps: tableSpinProps, isTree: mode === TableMode.tree },
                    this.getHeader(),
                    React.createElement(TableQueryBar, { buttons: buttons, buttonsLimit: tableButtonsLimit, pagination: pagination, queryFields: queryFields, clientExportQuantity: clientExportQuantity, summaryBar: summaryBar, dynamicFilterBar: dynamicFilterBar, queryFieldsLimit: queryFieldsLimit, summaryBarFieldWidth: summaryBarFieldWidth, summaryFieldsLimit: summaryFieldsLimit, filterBarFieldName: filterBarFieldName, filterBarPlaceholder: filterBarPlaceholder, treeQueryExpanded: treeQueryExpanded, searchCode: searchCode }),
                    this.getValidationErrors(),
                    React.createElement(Spin, Object.assign({}, tableSpinProps, { key: "content" }),
                        React.createElement("div", Object.assign({}, this.getOtherProps()),
                            React.createElement("div", { className: classNames(`${prefixCls}-content`, { [`${prefixCls}-content-overflow`]: isStickySupport() && overflowX && !overflowY }), ref: this.saveContentRef, onScroll: this.handleBodyScroll },
                                !isStickySupport() && isAnyColumnsLeftLock && overflowX && this.getLeftFixedTable(),
                                content,
                                !isStickySupport() && isAnyColumnsRightLock && overflowX && this.getRightFixedTable()),
                            isStickySupport() && overflowX && React.createElement(StickyShadow, { position: "left" }),
                            isStickySupport() && overflowX && React.createElement(StickyShadow, { position: "right" }),
                            React.createElement("div", { ref: this.saveResizeRef, className: `${prefixCls}-split-line` }))),
                    this.getFooter(),
                    this.getPagination(TablePaginationPosition.bottom)))));
    }
    reorderDataSet(startIndex, endIndex) {
        const { dataSet } = this.tableStore;
        dataSet.move(startIndex, endIndex);
    }
    /**
     * 触发组合或拖拽排序, 移除原纪录
     * @param currentRecord
     */
    removeSourceRecord(currentRecord) {
        const { parent } = currentRecord;
        if (parent) {
            const { children } = parent;
            if (children) {
                const index = children.indexOf(currentRecord);
                if (index !== -1) {
                    children.splice(index, 1);
                }
                if (!children.length) {
                    parent.children = undefined;
                }
            }
        }
    }
    handleDragStart(initial, provided) {
        const { dragDropContextProps } = this.props;
        const { rowMetaData, isTree } = this.tableStore;
        if (isTree && rowMetaData) {
            const { source } = initial;
            const currentRecord = rowMetaData[source.index].record;
            if (currentRecord.children && currentRecord.children.length) {
                currentRecord.isExpanded = false;
            }
        }
        if (dragDropContextProps && dragDropContextProps.onDragStart) {
            dragDropContextProps.onDragStart(initial, provided);
        }
    }
    handleDragEnd(resultDrag, provided) {
        const { dataSet, rowMetaData, isTree } = this.tableStore;
        const { parentField, idField, childrenField } = dataSet.props;
        const { onDragEnd, onDragEndBefore, dragDropContextProps } = this.props;
        let resultBefore = resultDrag;
        if (onDragEndBefore) {
            const result = onDragEndBefore(this.tableStore.dataSet, toJS(this.tableStore.columns), resultDrag, provided);
            if (result === false) {
                return;
            }
            if (isDropresult(result)) {
                resultBefore = result;
            }
        }
        if (resultBefore && isTree && rowMetaData) {
            const { destination, source, combine } = resultBefore;
            const currentRecord = rowMetaData[source.index].record;
            // 平铺数据
            if (!childrenField && parentField && idField) {
                // 拖拽组合关联
                if (combine) {
                    const parentRecord = dataSet.find(record => String(record.key) === combine.draggableId);
                    currentRecord.set(parentField, parentRecord.get(idField));
                    parentRecord.isExpanded = true;
                }
                // 拖拽排序更新
                if (destination && destination.index !== source.index && rowMetaData) {
                    const destinationRecord = rowMetaData[destination.index].record;
                    const { parent } = destinationRecord;
                    if (parent) {
                        if (currentRecord.parent !== parent && parent !== currentRecord && parent.children) {
                            currentRecord.set(parentField, parent.get(idField));
                        }
                        else if (parent.children) {
                            const childIndex = parent.children.indexOf(destinationRecord);
                            this.removeSourceRecord(currentRecord);
                            parent.children.splice(childIndex, 0, currentRecord);
                        }
                    }
                    else {
                        currentRecord.set(parentField, undefined);
                        dataSet.move(currentRecord.index, destinationRecord.index);
                    }
                }
            }
            // 树形数据
            if (childrenField) {
                // 拖拽组合关联
                if (combine) {
                    const parentRecord = dataSet.find(record => String(record.key) === combine.draggableId);
                    this.removeSourceRecord(currentRecord);
                    currentRecord.parent = parentRecord;
                    if (parentRecord.children && parentRecord.children.length) {
                        parentRecord.children.unshift(currentRecord);
                    }
                    else {
                        parentRecord.children = [currentRecord];
                    }
                    parentRecord.isExpanded = true;
                }
                // 拖拽排序更新
                if (destination && destination.index !== source.index && rowMetaData) {
                    const destinationRecord = rowMetaData[destination.index].record;
                    const { parent } = destinationRecord;
                    if (parent) {
                        if (currentRecord.parent !== parent && parent !== currentRecord && parent.children) {
                            const childIndex = parent.children.indexOf(destinationRecord);
                            this.removeSourceRecord(currentRecord);
                            currentRecord.parent = parent;
                            parent.children.splice(childIndex, 0, currentRecord);
                        }
                        else if (parent.children) {
                            const childIndex = parent.children.indexOf(destinationRecord);
                            this.removeSourceRecord(currentRecord);
                            parent.children.splice(childIndex, 0, currentRecord);
                        }
                    }
                    else {
                        if (currentRecord.parent) {
                            this.removeSourceRecord(currentRecord);
                            currentRecord.parent = undefined;
                        }
                        dataSet.move(currentRecord.index, destinationRecord.index);
                    }
                }
            }
        }
        else if (resultBefore && resultBefore.destination) {
            if (resultBefore.source.index !== resultBefore.destination.index) {
                // 非树形拖拽排序
                this.reorderDataSet(resultBefore.source.index, resultBefore.destination.index);
            }
        }
        /**
         * 相应变化后的数据
         */
        const onDragEndFun = onDragEnd || (dragDropContextProps ? dragDropContextProps.onDragEnd : undefined);
        if (onDragEndFun) {
            onDragEndFun(this.tableStore.dataSet, toJS(this.tableStore.columns), resultBefore, provided);
        }
    }
    handleBodyScroll(e) {
        const { currentTarget } = e;
        const handle = () => {
            this.handleBodyScrollTop(e, currentTarget);
            this.handleBodyScrollLeft(e, currentTarget);
        };
        if (isStickySupport()) {
            handle();
        }
        else {
            e.persist();
            this.scrollId = raf(handle);
        }
    }
    handleBodyScrollTop(e, currentTarget) {
        const { target } = e;
        const { tableStore } = this;
        if (
        // (isStickySupport() && !tableStore.virtual) ||
        // (![TableHeightType.fixed, TableHeightType.flex].includes(tableStore.heightType)) ||
        !tableStore.overflowY ||
            currentTarget !== target ||
            target === this.tableFootWrap) {
            return;
        }
        const { scrollTop } = target;
        this.setScrollTop(scrollTop, target);
    }
    handleBodyScrollLeft(e, currentTarget) {
        const { target } = e;
        const { tableStore } = this;
        if (!tableStore.overflowX ||
            currentTarget !== target ||
            target === this.fixedColumnsBodyRight ||
            target === this.fixedColumnsBodyLeft) {
            return;
        }
        const { scrollLeft } = target;
        this.setScrollLeft(scrollLeft, target);
    }
    setScrollPositionClassName(target) {
        const { tableStore } = this;
        if (tableStore.isAnyColumnsLock && tableStore.overflowX) {
            const node = target || this.tableBodyWrap || this.tableContentWrap;
            if (node) {
                const scrollToLeft = node.scrollLeft === 0;
                const table = node.querySelector('table');
                const scrollToRight = table && node.scrollLeft >= table.offsetWidth - node.offsetWidth;
                if (scrollToLeft && scrollToRight) {
                    this.setScrollPosition(ScrollPosition.both);
                }
                else if (scrollToLeft) {
                    this.setScrollPosition(ScrollPosition.left);
                }
                else if (scrollToRight) {
                    this.setScrollPosition(ScrollPosition.right);
                }
                else {
                    this.setScrollPosition(ScrollPosition.middle);
                }
            }
        }
    }
    setScrollPosition(position) {
        const { tableStore } = this;
        if (tableStore.scrollPosition !== position) {
            tableStore.scrollPosition = position;
        }
    }
    renderTable(hasHeader, hasBody, hasFooter, lock, virtual) {
        const { tableStore } = this;
        const columnGroups = (() => {
            if (lock === ColumnLock.right) {
                return tableStore.rightColumnGroups;
            }
            if (lock) {
                return tableStore.leftColumnGroups;
            }
            return tableStore.columnGroups;
        })();
        /* eslint-disable react/no-this-in-sfc */
        const wrapper = (snapshot, dragRowHeight) => (React.createElement(TableWrapper, { key: "tableWrapper", lock: lock, hasBody: hasBody, hasHeader: hasHeader, hasFooter: hasFooter, columnGroups: columnGroups },
            hasHeader && this.getTableHeader(lock),
            hasBody && this.getTableBody(columnGroups, lock, snapshot, dragRowHeight),
            hasFooter && this.getTableFooter(columnGroups, lock)));
        /* eslint-enable react/no-this-in-sfc */
        const virtualWrapper = virtual ? React.createElement(VirtualWrapper, null, wrapper) : wrapper();
        if (hasBody && tableStore.rowDraggable) {
            const { dragDropContextProps } = this.props;
            return (React.createElement(DragDropContext, Object.assign({}, dragDropContextProps, { onDragStart: this.handleDragStart, onDragEnd: this.handleDragEnd }), virtualWrapper));
        }
        return virtualWrapper;
    }
    getHeader() {
        const { props: { header, dataSet }, } = this;
        if (header) {
            const { prefixCls } = this;
            const data = dataSet ? dataSet.records : [];
            return (React.createElement("div", { key: "header", className: `${prefixCls}-header` }, typeof header === 'function' ? header(data) : header));
        }
    }
    getFooter() {
        const { props: { footer, dataSet }, } = this;
        if (footer) {
            const { prefixCls } = this;
            const data = dataSet ? dataSet.records : [];
            return (React.createElement("div", { key: "footer", className: `${prefixCls}-footer` }, typeof footer === 'function' ? footer(data) : footer));
        }
    }
    getPagination(position) {
        const { props: { dataSet, selectionMode }, prefixCls, tableStore: { pagination, showSelectionTips }, } = this;
        if (pagination !== false && dataSet && dataSet.paging) {
            const paginationPosition = getPaginationPosition(pagination);
            if (paginationPosition === TablePaginationPosition.both || paginationPosition === position) {
                const paginationProps = omit(pagination, 'position');
                return (React.createElement(Pagination, Object.assign({ key: `pagination-${position}` }, paginationProps, { className: classNames(`${prefixCls}-pagination`, paginationProps.className, { [`${prefixCls}-pagination-with-selection-tips`]: showSelectionTips }), dataSet: dataSet }), selectionMode !== SelectionMode.none && dataSet.selection === DataSetSelection.multiple && React.createElement(SelectionTips, null)));
            }
        }
    }
    getTable(lock) {
        const { props, tableStore } = this;
        const { overflowX, heightType, hasFooter: footer, virtual } = tableStore;
        let tableHead;
        let tableBody;
        let tableFooter;
        if (virtual || (!isStickySupport() && overflowX) || [TableHeightType.flex, TableHeightType.fixed].includes(heightType) || tableStore.height !== undefined) {
            const { prefixCls } = this;
            let tableHeadRef;
            let tableBodyRef;
            let tableFootRef;
            if (!lock) {
                tableHeadRef = node => (this.tableHeadWrap = node);
                tableFootRef = node => (this.tableFootWrap = node);
                tableBodyRef = node => (this.tableBodyWrap = node);
            }
            else if (lock === ColumnLock.right) {
                tableBodyRef = (node) => {
                    this.fixedColumnsBodyRight = node;
                    if (node) {
                        this.syncSize();
                    }
                };
            }
            else {
                tableBodyRef = (node) => {
                    this.fixedColumnsBodyLeft = node;
                    if (node) {
                        this.syncSize();
                    }
                };
            }
            const style = lock ? {
                width: pxToRem(lock === ColumnLock.right
                    ? (tableStore.rightColumnGroups.width - 1 + (tableStore.overflowY ? measureScrollbar() : 0))
                    : tableStore.leftColumnGroups.width),
                marginLeft: lock === ColumnLock.right ? pxToRem(1) : undefined,
            } : undefined;
            tableHead = (React.createElement("div", { key: "tableHead", ref: tableHeadRef, className: `${prefixCls}-head`, style: style }, this.renderTable(true, false, false, lock)));
            if (lock !== ColumnLock.right || !onlyCustomizedColumn(tableStore)) {
                const body = this.renderTable(false, true, false, lock, virtual);
                tableBody = (React.createElement(TableBody, { key: "tableBody", getRef: tableBodyRef, lock: lock, onScroll: this.handleBodyScroll, style: pick(props.style, ['maxHeight', 'minHeight']) }, body));
            }
            if (footer) {
                tableFooter = (React.createElement("div", { key: "tableFooter", ref: tableFootRef, className: `${prefixCls}-foot`, style: style, onScroll: this.handleBodyScroll }, this.renderTable(false, false, true, lock)));
            }
        }
        else {
            tableBody = this.renderTable(true, true, footer, lock);
        }
        return [tableHead, tableBody, tableFooter];
    }
    getLeftFixedTable() {
        return (React.createElement(StickyShadow, { position: "left" }, this.getTable(ColumnLock.left)));
    }
    getRightFixedTable() {
        return (React.createElement(StickyShadow, { position: "right" }, this.getTable(ColumnLock.right)));
    }
    getTableBody(columnGroups, lock, snapshot, dragRowHeight) {
        const { tableStore: { performanceEnabled }, props: { bodyExpandable, }, } = this;
        const tBodyProps = {
            key: 'tbody',
            lock,
            columnGroups,
            snapshot,
            dragRowHeight,
        };
        const body = bodyExpandable ? React.createElement(ExpandableTableTBody, Object.assign({}, tBodyProps)) : React.createElement(TableTBody, Object.assign({}, tBodyProps));
        return performanceEnabled ? React.createElement(Profiler, null, body) : body;
    }
    getTableHeader(lock) {
        const { showHeader } = this.props;
        return showHeader || this.tableStore.customizable ? (React.createElement(TableHeader, { key: "thead", lock: lock })) : undefined;
    }
    getTableFooter(columnGroups, lock) {
        return React.createElement(TableFooter, { key: "tfoot", lock: lock, columnGroups: columnGroups });
    }
    getStyleHeight() {
        const { style } = this.props;
        if (style) {
            return toPx(style.height);
        }
    }
    syncSizeInFrame(width = this.getWidth()) {
        if (this.nextFrameActionId !== undefined) {
            raf.cancel(this.nextFrameActionId);
        }
        this.nextFrameActionId = raf(this.syncSize.bind(this, width));
    }
    syncSize(width = this.getWidth()) {
        const { element, tableStore } = this;
        if (tableStore.hidden || !element.offsetParent) {
            return;
        }
        if (element) {
            tableStore.width = Math.floor(width);
            const { prefixCls } = this;
            const tableHeader = tableStore.props.showHeader ? element.querySelector(`.${prefixCls}-thead`) : null;
            const tableFooter = tableStore.hasFooter ? element.querySelector(`.${prefixCls}-tfoot`) : null;
            tableStore.screenHeight = document.documentElement.clientHeight;
            tableStore.headerHeight = tableHeader ? getHeight(tableHeader) : 0;
            tableStore.footerHeight = tableFooter ? getHeight(tableFooter) : 0;
        }
        this.setScrollPositionClassName();
    }
    initDefaultExpandedRows() {
        const { tableStore, props: { dataSet, defaultRowExpanded }, } = this;
        if (tableStore.isTree && defaultRowExpanded) {
            dataSet.forEach((record) => {
                if (record.children) {
                    record.isExpanded = true;
                }
            });
        }
    }
    handleHeightTypeChange(immediate) {
        this.disconnect();
        if (immediate) {
            this.syncSize();
        }
        else {
            this.syncSizeInFrame();
        }
        this.connect();
    }
    getWidth() {
        const { wrapper } = this;
        if (wrapper) {
            return wrapper.offsetWidth;
        }
        return 0;
    }
    setScrollTop(scrollTop, target) {
        if (scrollTop !== this.lastScrollTop) {
            const { tableStore, fixedColumnsBodyLeft, tableBodyWrap, fixedColumnsBodyRight, tableContentWrap } = this;
            if (fixedColumnsBodyLeft && target !== fixedColumnsBodyLeft) {
                fixedColumnsBodyLeft.scrollTop = scrollTop;
            }
            if (tableBodyWrap && target !== tableBodyWrap) {
                tableBodyWrap.scrollTop = scrollTop;
            }
            if (fixedColumnsBodyRight && target !== fixedColumnsBodyRight) {
                fixedColumnsBodyRight.scrollTop = scrollTop;
            }
            if (tableContentWrap && target !== tableContentWrap) {
                tableContentWrap.scrollTop = scrollTop;
            }
            this.lastScrollTop = scrollTop;
            tableStore.setLastScrollTop(scrollTop);
            if (target) {
                const { onScrollTop = noop } = this.props;
                onScrollTop(scrollTop);
            }
        }
    }
    setScrollLeft(scrollLeft, target) {
        if (scrollLeft !== this.lastScrollLeft) {
            const { tableStore } = this;
            const { tableHeadWrap, tableBodyWrap, tableFootWrap, tableContentWrap } = this;
            if (isStickySupport()) {
                tableStore.editors.forEach((editor) => {
                    if (editor.lock && editor.cellNode) {
                        if (tableStore.inlineEdit) {
                            editor.alignEditor(editor.cellNode);
                        }
                        else {
                            editor.hideEditor();
                        }
                    }
                });
            }
            if (tableHeadWrap && target !== tableHeadWrap) {
                tableHeadWrap.scrollLeft = scrollLeft;
            }
            if (tableBodyWrap && target !== tableBodyWrap) {
                tableBodyWrap.scrollLeft = scrollLeft;
            }
            if (tableFootWrap && target !== tableFootWrap) {
                tableFootWrap.scrollLeft = scrollLeft;
            }
            if (tableContentWrap && target !== tableContentWrap) {
                tableContentWrap.scrollLeft = scrollLeft;
            }
            this.setScrollPositionClassName(target);
            this.lastScrollLeft = scrollLeft;
            if (target) {
                const { onScrollLeft = noop } = this.props;
                onScrollLeft(scrollLeft);
            }
        }
    }
    getValidationErrors() {
        const { validationSelfErrors: error } = this.props.dataSet;
        const showError = this.showDataSetError && error && error.length;
        return (React.createElement(Animate, { transitionName: "slide-down", className: classNames(`${this.prefixCls}-error`), hiddenProp: "hidden", component: "div" }, showError && (React.createElement("div", { hidden: !showError, className: classNames(`${this.prefixCls}-error-content`) },
            React.createElement("div", null,
                React.createElement(Icon, { type: "cancel" }),
                error ? error[0].message : null),
            React.createElement(Icon, { type: "close", onClick: this.clearError })))));
    }
    clearError() {
        this.handleDataSetSelfValidate({ valid: true });
    }
};
Table.displayName = 'Table';
Table.Column = Column;
Table.FilterBar = FilterBar;
Table.AdvancedQueryBar = AdvancedQueryBar;
Table.ProfessionalBar = ProfessionalBar;
Table.DynamicFilterBar = DynamicFilterBar;
Table.ToolBar = ToolBar;
Table.TableRow = TableRow;
Table.TableHeaderCell = TableHeaderCell;
Table.propTypes = {
    columns: PropTypes.array,
    /**
     * 表头
     */
    header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 是否显示表头
     */
    showHeader: PropTypes.bool,
    /**
     * 是否显示表头
     */
    showRemovedRow: PropTypes.bool,
    /**
     * 表脚
     */
    footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 是否显示边框
     * @default true
     */
    border: PropTypes.bool,
    /**
     * 可编辑单元格边框
     */
    columnEditorBorder: PropTypes.bool,
    /**
     * 功能按钮
     * 可选值：`add` `delete` `remove` `save` `query` `expandAll` `collapseAll` 或 自定义按钮
     */
    buttons: PropTypes.arrayOf(PropTypes.oneOfType([
        buttonsEnumType,
        PropTypes.arrayOf(PropTypes.oneOfType([buttonsEnumType, PropTypes.object])),
        PropTypes.node,
    ])),
    buttonsLimit: PropTypes.number,
    /**
     * 自定义查询字段组件
     * 默认会根据queryDataSet中定义的field类型自动匹配组件， 匹配类型如下
     * lovCode => Lov
     * lookupCode => Select
     * type:number => NumberField
     * type:date => DatePicker
     * type:dateTime => DatePicker[mode=dateTime]
     * type:week => DatePicker[mode=week]
     * default => TextField
     */
    queryFields: PropTypes.object,
    /**
     * 头部显示的查询字段的数量，超出限制的查询字段放入弹出窗口
     * @default 1
     */
    queryFieldsLimit: PropTypes.number,
    /**
     * 头部显示的汇总字段的数量，超出限制的查询字段放入弹出窗口
     * @default 3
     */
    summaryFieldsLimit: PropTypes.number,
    /**
     * 头部显示的汇总字段单个宽度
     * @default 170
     */
    summaryBarFieldWidth: PropTypes.number,
    /**
     * 显示查询条
     * @default true
     */
    queryBar: PropTypes.oneOfType([
        PropTypes.oneOf([
            TableQueryBarType.advancedBar,
            TableQueryBarType.normal,
            TableQueryBarType.bar,
            TableQueryBarType.none,
            TableQueryBarType.professionalBar,
            TableQueryBarType.filterBar,
        ]),
        PropTypes.func,
    ]),
    useMouseBatchChoose: PropTypes.bool,
    /**
     * 行高
     * @default 30
     */
    rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
    alwaysShowRowBox: PropTypes.bool,
    showSelectionTips: PropTypes.bool,
    showSelectionCachedButton: PropTypes.bool,
    showCachedSelection: PropTypes.bool,
    onShowCachedSelectionChange: PropTypes.func,
    showAllPageSelectionButton: PropTypes.bool,
    defaultRowExpanded: PropTypes.bool,
    expandRowByClick: PropTypes.bool,
    indentSize: PropTypes.number,
    filter: PropTypes.func,
    mode: PropTypes.oneOf([TableMode.list, TableMode.tree]),
    editMode: PropTypes.oneOf([TableEditMode.inline, TableEditMode.cell]),
    filterBarFieldName: PropTypes.string,
    filterBarPlaceholder: PropTypes.string,
    highLightRow: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([HighLightRowType.focus, HighLightRowType.click])]),
    selectedHighLightRow: PropTypes.bool,
    parityRow: PropTypes.bool,
    autoMaxWidth: PropTypes.bool,
    /**
     * 设置drag框体位置
     */
    dragColumnAlign: PropTypes.oneOf([DragColumnAlign.left, DragColumnAlign.right]),
    /**
     * 开启列拖拽，但是无法使用宽度拖拽
     */
    columnDraggable: PropTypes.bool,
    /**
     * 开启行拖拽
     */
    rowDraggable: PropTypes.bool,
    columnsDragRender: PropTypes.object,
    expandIcon: PropTypes.func,
    expandIconAsCell: PropTypes.bool,
    expandIconColumnIndex: PropTypes.number,
    rowDragRender: PropTypes.object,
    onDragEndBefore: PropTypes.func,
    /**
     * 新增的组合键开关闭
     */
    keyboard: PropTypes.bool,
    /**
     * 是否单独处理column footer
     */
    autoFootHeight: PropTypes.bool,
    /**
     * 客户端查询导出，查询数目设置
     */
    clientExportQuantity: PropTypes.number,
    /**
     * 可以修改由于样式导致的虚拟高度和rowHeight不一致
     */
    virtualRowHeight: PropTypes.number,
    ...DataSetComponent.propTypes,
};
Table.defaultProps = {
    suffixCls: 'table',
    tabIndex: 0,
    selectionMode: SelectionMode.rowbox,
    rowBoxPlacement: RowBoxPlacement.start,
    queryFields: {},
    defaultRowExpanded: false,
    expandRowByClick: false,
    indentSize: 15,
    summaryFieldsLimit: 3,
    summaryBarFieldWidth: 170,
    filterBarFieldName: 'params',
    virtualSpin: false,
    autoHeight: false,
    autoMaxWidth: true,
    autoFootHeight: false,
    clientExportQuantity: 100,
    showSelectionCachedButton: true,
    showHeader: true,
};
__decorate([
    observable
], Table.prototype, "showDataSetError", void 0);
__decorate([
    autobind
], Table.prototype, "saveResizeRef", null);
__decorate([
    autobind
], Table.prototype, "saveContentRef", null);
__decorate([
    autobind,
    action
], Table.prototype, "handleResize", null);
__decorate([
    autobind
], Table.prototype, "handleWindowResize", null);
__decorate([
    autobind,
    action
], Table.prototype, "handleDataSetLoad", null);
__decorate([
    autobind
], Table.prototype, "handleDataSetCreate", null);
__decorate([
    autobind
], Table.prototype, "handleDataSetValidate", null);
__decorate([
    autobind
], Table.prototype, "handleDataSetSelfValidate", null);
__decorate([
    autobind
], Table.prototype, "handleDataSetReset", null);
__decorate([
    autobind
], Table.prototype, "handleKeyDown", null);
__decorate([
    autobind,
    action
], Table.prototype, "syncParentSize", null);
__decorate([
    action
], Table.prototype, "reorderDataSet", null);
__decorate([
    action
], Table.prototype, "removeSourceRecord", null);
__decorate([
    autobind,
    action
], Table.prototype, "handleDragStart", null);
__decorate([
    autobind,
    action
], Table.prototype, "handleDragEnd", null);
__decorate([
    autobind
], Table.prototype, "handleBodyScroll", null);
__decorate([
    action
], Table.prototype, "setScrollPosition", null);
__decorate([
    autobind,
    action
], Table.prototype, "syncSize", null);
__decorate([
    action
], Table.prototype, "initDefaultExpandedRows", null);
__decorate([
    autobind,
    action
], Table.prototype, "clearError", null);
Table = __decorate([
    observer
], Table);
export default Table;
